import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Map as ReactMapGL } from 'react-map-gl/maplibre';
import DeckGL, { IconLayer } from 'deck.gl';

import { Box, Typography, DialogContent, DialogTitle, Grid, FormControlLabel, Checkbox } from '@mui/material';

import { DialogFooter, DialogActionButton } from 'components/Dialog';

import { useStyles } from './styles';
import { compose } from 'utils/compose';
import { EDIT_NOTICE } from '../../constants/verbiage';
import MARKERS from '../../assets/svg/markers.svg';
import withMediaQuery from '../../constants/mediaQuery';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

class EditLocationDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      useMyLocation: false,
      userDeniedLocation: true,
      currentLocation: {},
      viewport: {
        latitude: 37.772402,
        longitude: -122.414265,
        zoom: 16,
        maxZoom: 20,
        minZoom: 16,
      },
      mapStyle: `https://api.maptiler.com/maps/topo/style.json?key=${encodeURIComponent(import.meta.env.REACT_APP_MAP_TILER_KEY)}`,
    };
  }

  componentDidMount() {
    const { delivery, editedDeliveryData } = this.props.store.deliveryStore;
    const { shipment } = delivery;

    // get user current location
    navigator.geolocation.getCurrentPosition(
      (location) =>
        this.setState({
          currentLocation: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
            accuracy: location.coords.accuracy,
          },
          userDeniedLocation: false,
        }),
      (error) => this.setState({ userDeniedLocation: true }),
    );

    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude: editedDeliveryData.dropoff_latitude || shipment.dropoff_address.lat,
        longitude: editedDeliveryData.dropoff_longitude || shipment.dropoff_address.lng,
      },
    });
  }

  handleUseCurrentLocation = (checked) => {
    this.setState({ useMyLocation: checked });
    if (checked) {
      const { viewport, currentLocation } = this.state;
      this.setState({
        viewport: {
          ...viewport,
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
        },
      });
    }
  };

  save = () => {
    const { deliveryStore } = this.props.store;
    const { viewport } = this.state;

    deliveryStore.setEditedDeliveryField('dropoff_latitude', viewport.latitude);
    deliveryStore.setEditedDeliveryField('dropoff_longitude', viewport.longitude);
    deliveryStore.saveAndCloseDialog();
  };

  renderMarker = (location, id) => {
    const lat = location.lat || location.latitude;
    const lng = location.lng || location.longitude;
    const iconMapping = {
      shipment: { x: 690, y: 220, width: 60, height: 60 },
      viewport: { x: 160, y: 0, width: 160, height: 210, anchorY: 210 },
      mylocation: { x: 530, y: 220, width: 60, height: 60 },
    };

    return new IconLayer({
      id: id,
      data: [
        {
          location: [lng, lat],
          size: id === 'viewport' ? 40 : 25,
          icon: id,
        },
      ],
      pickable: true,
      iconAtlas: MARKERS,
      iconMapping: iconMapping,
      getIcon: (d) => d.icon,
      sizeScale: 1,
      opacity: 1,
      visible: true,
      billboard: false,
      getPosition: (d) => d.location,
      getSize: (d) => d.size,
    });
  };

  render() {
    const { useMyLocation, currentLocation, userDeniedLocation, viewport, mapStyle } = this.state;
    const { classes, store, mediaQuery } = this.props;
    const { deliveryStore } = store;
    const { delivery, tokenRemainingTimeString, editedDeliveryData } = deliveryStore;

    if (!delivery) return null;

    const { shipment } = delivery;
    let layers = [this.renderMarker(shipment.dropoff_address, 'shipment')];
    if (!userDeniedLocation) {
      layers = layers.concat([this.renderMarker(currentLocation, 'mylocation')]);
    }
    layers = layers.concat([this.renderMarker(viewport, 'viewport')]);

    return (
      <Box sx={classes.container}>
        <DialogTitle sx={classes.titleBox}>
          <Grid
            container
            justifyContent="space-between"
            alignItems={mediaQuery.isMobile ? 'flex-start' : 'center'}
            direction={mediaQuery.isMobile ? 'column' : 'row'}
            spacing={mediaQuery.isMobile ? 1 : 0}
          >
            <Grid sx={classes.dialogTitle}>
              <span style={{ fontWeight: 600 }}>Edit Location Pin</span>
            </Grid>
            <Grid sx={classes.dialogTimer}>
              <Box component="span" sx={classes.underlined}>
                Session time:
              </Box>
              <Box component="span" sx={classes.timerCounter}>
                {tokenRemainingTimeString}
              </Box>
            </Grid>
          </Grid>
          <Box pt={2} pb={mediaQuery.isMobile ? 2 : 3} sx={classes.noticeBox}>
            <span style={{ fontWeight: 600 }}>{EDIT_NOTICE}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box pt={1} pb={mediaQuery.isMobile ? 1 : 3}>
            <Typography sx={{ ...classes.darkGrayText, ...classes.infoBox }}>
              Provide us a pin from your exact location to help us delivering your package!
            </Typography>
          </Box>
          <Box mb={3} mx={mediaQuery.isMobile ? -3 : 0} height={350}>
            <ReactMapGL
              {...viewport}
              mapStyle={mapStyle}
              onMove={(evt) => this.setState({ viewport: evt.viewState })}
              style={{ width: '100%', height: '100%' }}
              attributionControl={false}
            >
              <DeckGL layers={layers} viewState={viewport} style={{ pointerEvents: 'none' }} />
            </ReactMapGL>
          </Box>
          <Box sx={userDeniedLocation ? classes.disabled : undefined}>
            <FormControlLabel
              disabled={userDeniedLocation}
              control={
                <Checkbox
                  checked={useMyLocation}
                  onChange={(e) => this.handleUseCurrentLocation(e.target.checked)}
                  value="use_my_location"
                  color="primary"
                />
              }
              label={
                <Box component="span" sx={classes.darkGrayText}>
                  Use my current location
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogFooter>
          <DialogActionButton onClick={() => deliveryStore.closeEditDialog()} variant="outlined" color="primary" disableElevation>
            Cancel
          </DialogActionButton>
          <DialogActionButton onClick={this.save} variant="contained" color="primary" disableElevation>
            Save
          </DialogActionButton>
        </DialogFooter>
      </Box>
    );
  }
}

const EditLocationDialogCompose = compose(inject('store'), observer)(EditLocationDialog);

export default withUseStyles(withMediaQuery()(EditLocationDialogCompose));
